import React, { useState } from "react";
import { Box, Button, Typography, IconButton, TextField } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloseIcon from "@mui/icons-material/Close";
import { styled, useTheme } from "@mui/material/styles";
import API_ENDPOINTS from "../../../apis/endpoints";
import useAuth from "../../../context";

const Input = styled("input")({
    display: "none",
});

const AiAvatarUpload = ({ onNext }) => {
    const auth = useAuth();
    const [avatarName, setAvatarName] = useState("");
    const [uploadedImages, setUploadedImages] = useState([]);
    const theme = useTheme();
    const storedDeviceId = localStorage.getItem("device-id");

    const handleCreateModel = async () => {
        if (uploadedImages.length < 5) {
            alert("Please upload at least 5 images to continue.");
            return;
        }

        try {
            const modelResponse = await fetch(
                `${API_ENDPOINTS.AI_MODEL_AVATAR}`,
                {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${auth.accessToken}`,
                        "x-cc-device-id": storedDeviceId,
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({ name: avatarName }),
                }
            );

            if (!modelResponse.ok) {
                throw new Error("Failed to create avatar model");
            }

            const modelData = await modelResponse.json();
            const folderId = modelData.data.id;
            const folderType = modelData.data.service;

            // Pass data to the next step
            onNext({ folderId, folderType, files: uploadedImages });
        } catch (error) {
            console.error("Error in handleCreateModel:", error);
        }
    };

    const handleAvatarNameChange = (event) => {
        setAvatarName(event.target.value);
    };

    const handleImageUpload = (event) => {
        const files = Array.from(event.target.files);
        const validFiles = files.filter((file) =>
            file.type.startsWith("image/")
        );
        setUploadedImages((prevImages) =>
            [...prevImages, ...validFiles].slice(0, 50)
        );
    };

    const handleRemoveImage = (index) => {
        setUploadedImages((prevImages) =>
            prevImages.filter((_, i) => i !== index)
        );
    };

    return (
        <Box sx={{ padding: "20px" }}>
            <Typography variant="highlightSemibold">
                Avatar Name & Upload Images
            </Typography>
            <br /> <br /> <br />
            <Typography variant="contentEmphasis">Avatar Name</Typography>
            <TextField
                fullWidth
                value={avatarName}
                onChange={handleAvatarNameChange}
                placeholder="Enter the name of the avatar"
                margin="normal"
            />
            <br /> <br />
            <Typography variant="contentEmphasis">
                Upload Images ({uploadedImages.length}/50)
            </Typography>
            <br />
            <Typography variant="supportRegular" sx={{ marginBottom: "2rem" }}>
                Must upload 50 images here with different angles so that AI will
                model them to create avatar.
            </Typography>
            <br />
            {uploadedImages.length > 0 && (
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(5, 1fr)",
                        gap: 2,
                    }}
                >
                    {uploadedImages.map((image, index) => (
                        <Box
                            key={index}
                            sx={{
                                position: "relative",
                            }}
                        >
                            <img
                                src={URL.createObjectURL(image)}
                                alt={`Uploaded ${index + 1}`}
                                style={{
                                    width: "100%",
                                    height: "100px",
                                    objectFit: "cover",
                                }}
                            />
                            <IconButton
                                sx={{ position: "absolute", top: 0, right: 0 }}
                                size="small"
                                onClick={() => handleRemoveImage(index)}
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    ))}
                </Box>
            )}
            {uploadedImages.length < 50 && (
                <Box
                    sx={{
                        border: "2px dashed grey",
                        borderRadius: 2,
                        p: 5,
                        textAlign: "center",
                        cursor: "pointer",
                        backgroundColor: theme.palette.primary[50],
                        color: theme.palette.primary[900],
                    }}
                >
                    <Input
                        accept="image/*"
                        id="image-upload"
                        type="file"
                        multiple
                        onChange={handleImageUpload}
                    />
                    <label htmlFor="image-upload">
                        <Button
                            variant="contained"
                            component="span"
                            startIcon={<CloudUploadIcon />}
                        >
                            Upload or Drag & Drop Images
                        </Button>
                    </label>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                        Only image files are accepted
                    </Typography>
                </Box>
            )}
            <Button
                variant="contained"
                sx={{ width: "25%", marginTop: "3rem", marginBottom: "2rem" }}
                onClick={handleCreateModel}
                disabled={
                    avatarName.trim() === "" || uploadedImages.length < 50
                } // Disable button if conditions are not met
            >
                Continue
            </Button>
        </Box>
    );
};

export default AiAvatarUpload;
